'use client';

import { Box } from '@mui/material';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid-pro';
import GalleryPagination from './GalleryPagination';
import {
  DEFAULT_ANIMAL_DETAIL,
  TRow,
} from '@/hooks/studbook/animal/useAnimalFilter';
import useAnimalPrimaryImages from '@/hooks/studbook/useAnimalPrimaryImages';
import WithSkeleton from '../../utils/WithSkeleton';
import { Grid2 } from '@/primitives';
import AnimalCard from './AnimalCard';
import useResponsive from '@/hooks/studbook/common/useResponsive';
import {
  NAV_HEIGHT,
  PUBLIC_SEARCH_HEADER_SECTION,
  PUBLIC_SEARCH_HEADER_SECTION_MOBILE,
} from '@/configs/layout';
import { convertEntityToObject } from '@/utils/entity-detail';

type TProps = {
  loading: boolean;
  entities?: any[];
  dataGrid: {
    getRowId?: (row: any) => string | number;
    columns: any[];
    rows: any[] | undefined;
  };
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>,
  ) => void;
};

export default function PublicSearchGalleryView({
  dataGrid,
  loading,
  rowCount,
  paginationModel,
  entities,
  onPaginationModelChange,
}: TProps) {
  const { isMobile } = useResponsive();
  const getDetailDataByEntityDetailName = (
    row: TRow,
    entityDetailName: string,
  ) => {
    const column = dataGrid.columns.find(
      (col) => col.entityDetailName === entityDetailName,
    );
    if (!column) return { value: '', id: '' };

    return row[column.entityDetailName as keyof TRow];
  };

  const entityIds = (dataGrid.rows ?? [])
    .map((r) => r[DEFAULT_ANIMAL_DETAIL.ID])
    .map((d) => d.value);

  const { multipleImgData, error, isLoading } =
    useAnimalPrimaryImages(entityIds);

  const imgUrlByGuid = Object.fromEntries(
    (multipleImgData ?? []).map((d: any) => [d.entityGuid, d.imgUrl]),
  );

  return (
    <Box
      sx={{
        height: `calc(100vh - ${
          isMobile
            ? PUBLIC_SEARCH_HEADER_SECTION_MOBILE
            : PUBLIC_SEARCH_HEADER_SECTION
        }px - ${NAV_HEIGHT * 2}px)`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <WithSkeleton
        isLoading={loading}
        sx={{ margin: 'auto' }}
        useCircularProgress
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            padding: 1,
          }}
        >
          {!dataGrid?.rows || dataGrid.rows.length === 0 || isLoading ? (
            <></>
          ) : (
            <Grid2 container spacing={2}>
              {dataGrid.rows?.map((row: TRow, index) => {
                const guid = row[DEFAULT_ANIMAL_DETAIL.GUID];
                const entity = entities?.find(
                  (e) => e.entityGuid === guid.value,
                );
                const { objectByNames } = convertEntityToObject(entity);
                const name = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.ANIMAL_NAME,
                );
                const sex = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.SEX_CODE,
                );
                const identifier = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.ANIMAL_IDENT,
                );
                const birthDate = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.DATE_OF_BIRTH,
                );
                const color = {
                  id: name?.id,
                  value: objectByNames[DEFAULT_ANIMAL_DETAIL.COLOR],
                };
                const sire = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.SIRE,
                );
                const dam = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.DAM,
                );
                const breeder = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.BREEDER,
                );
                const owner = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.OWNER,
                );
                const status = getDetailDataByEntityDetailName(
                  row,
                  DEFAULT_ANIMAL_DETAIL.ANIMAL_STATUS,
                );

                return (
                  <Grid2
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={
                      dataGrid.getRowId ? dataGrid.getRowId(row) : row.id.value
                    }
                  >
                    <AnimalCard
                      key={index}
                      guid={guid}
                      imgUrl={imgUrlByGuid[guid.value]}
                      imgError={!!error}
                      isLoadingImg={isLoading}
                      name={name}
                      sex={sex}
                      identifier={identifier}
                      birthDate={birthDate}
                      color={color}
                      sire={sire}
                      dam={dam}
                      breeder={breeder}
                      owner={owner}
                      status={status}
                    />
                  </Grid2>
                );
              })}
            </Grid2>
          )}
        </Box>
      </WithSkeleton>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '6px 0',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        <GalleryPagination
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
        />
      </Box>
    </Box>
  );
}
